<template>
    <div>
        <!-- Terms of Service Dialog -->
        <v-dialog
            v-model="showTermsOfService"
            persistent
            max-width="600"
        >
            <v-card>
                <v-card-title class="headline">
                    Terms of Service
                </v-card-title>
                <v-card-text>
                    <p>
                        In order for materials to be accepted upon return, they must be in resalable condition: able to be redelivered and utilized for another order. In order to avoid possible disposal fees or additional freight costs, material that is not in resalable condition should not be returned.
                    </p>
                    <p>
                        The customer is responsible for return freight costs as well as a 15% restock fee on all materials. All materials made special are non-returnable.
                    </p>
                    <p class="mb-0">
                        The majority of our materials have a six-month return policy from the date on which they were invoiced. The products listed below have a three-month return policy due to their shorter shelf life.
                    </p>
                    <ul class="mb-3">
                        <li>Green-Lock Family Products</li>
                        <li>Tuff-Flash</li>
                        <li>KEE-Lock Mastic</li>
                        <li>Perma-Joint Sealant</li>
                    </ul>
                    <div>
                        <v-checkbox
                            v-model="agreeChecked"
                            label="I agree to the Terms of Service"
                            color="primary"
                            class="dialog-checkbox"
                        />
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        flat
                        @click="onBackClick"
                    >
                        Back
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!agreeChecked"
                        @click="showTermsOfService = false"
                    >
                        Continue
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <template v-if="!showSamplePhotos">
            <v-stepper
                alt-labels
                v-model="step"
                class="mb-3"
            >
                <v-stepper-header class="wizard-stepper">
                    <v-stepper-step
                        step="1"
                        :complete="step > 1"
                    >
                        Product Return Info
                    </v-stepper-step>
                    <v-divider />
                    <v-stepper-step
                        step="2"
                        :complete="step > 2"
                    >
                        Customer Info
                    </v-stepper-step>
                    <v-divider />
                    <v-stepper-step
                        step="3"
                        :complete="step > 3"
                    >
                        Additional Info
                    </v-stepper-step>
                    <v-divider />
                    <v-stepper-step
                        step="4"
                    >
                        Review
                    </v-stepper-step>
                </v-stepper-header>
                <v-stepper-items>
                    <!-- Product Info -->
                    <v-stepper-content step="1">
                        <v-form
                            ref="productForm"
                            @submit.prevent="onSubmitProductInfo"
                        >
                            <v-data-table
                                class="product-table"
                                :headers="headers"
                                :items="form.products"
                                hide-actions
                            >
                                <template v-slot:items="props">
                                    <td>{{ props.item.itemNumber }}</td>
                                    <td>{{ props.item.description }}</td>
                                    <td class="shrink-column">
                                        <v-text-field
                                            style="width: 100px"
                                            type="number"
                                            class="product-input"
                                            v-model.number="props.item.quantityToReturn"
                                            :rules="[
                                                rules.positiveNumber,
                                                rules.lessThanOrEqual(props.item.quantity)
                                            ]"
                                        >
                                            <template slot="append">
                                                <div class="mt-1">
                                                    /&nbsp;{{ props.item.quantity }}
                                                </div>
                                            </template>
                                        </v-text-field>
                                    </td>
                                </template>
                            </v-data-table>
                            <v-divider />
                            <v-layout
                                row
                                justify-end
                                mt-2
                            >
                                <v-flex shrink>
                                    <v-btn
                                        type="submit"
                                        color="primary"
                                    >
                                        Next
                                    </v-btn>
                                </v-flex>
                            </v-layout>
                        </v-form>
                    </v-stepper-content>
                    <!-- Customer Info -->
                    <v-stepper-content step="2">
                        <v-form
                            ref="customerForm"
                            @submit.prevent="onSubmitCustomerInfo"
                        >
                            <v-layout
                                row
                                wrap
                            >
                                <v-flex xs12>
                                    <v-text-field
                                        label="Customer Name"
                                        v-model="form.customerName"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex xs12>
                                    <h3 class="title">
                                        Pickup Location for Return
                                    </h3>
                                    <small>Information will be used to set up trucking for materials to be returned for credit.</small>
                                </v-flex>
                                <v-flex xs12>
                                    <v-text-field
                                        label="Company"
                                        v-model="form.company"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex xs12>
                                    <v-text-field
                                        label="Address"
                                        v-model="form.address"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex
                                    xs6
                                    sm4
                                >
                                    <v-text-field
                                        label="City"
                                        v-model="form.city"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex
                                    xs6
                                    sm4
                                >
                                    <v-select
                                        label="State"
                                        :items="states"
                                        item-text="name"
                                        item-value="iso"
                                        v-model="form.state"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex
                                    xs6
                                    sm4
                                >
                                    <v-text-field
                                        label="Zipcode"
                                        v-model="form.zipcode"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex xs12>
                                    <h3 class="title">
                                        Customer Contact for Return
                                    </h3>
                                </v-flex>
                                <v-flex
                                    xs12
                                >
                                    <v-text-field
                                        label="Name"
                                        v-model="form.contactName"
                                        :rules="[rules.required]"
                                    />
                                </v-flex>
                                <v-flex
                                    xs12
                                    sm6
                                >
                                    <v-text-field
                                        label="Phone"
                                        v-model="form.contactPhone"
                                        mask="phone"
                                        placeholder="(###) ### - ####"
                                        :rules="[rules.required, rules.phone]"
                                        validate-on-blur
                                    />
                                </v-flex>
                                <v-flex
                                    xs12
                                    sm6
                                >
                                    <v-text-field
                                        label="Email"
                                        type="email"
                                        v-model="form.contactEmail"
                                        hint="EMAIL required for BOL (bill of lading) can be emailed for pick up."
                                        persistent-hint
                                        :rules="[rules.required, rules.email]"
                                        validate-on-blur
                                    />
                                </v-flex>
                            </v-layout>

                            <!-- Actions -->
                            <v-divider />
                            <v-layout
                                row
                                justify-space-between
                                mt-2
                            >
                                <v-flex shrink>
                                    <v-btn
                                        type="button"
                                        icon
                                        @click="step = 1"
                                    >
                                        <v-icon>fa-arrow-left</v-icon>
                                    </v-btn>
                                </v-flex>
                                <v-flex shrink>
                                    <v-btn
                                        type="submit"
                                        color="primary"
                                    >
                                        Next
                                    </v-btn>
                                </v-flex>
                            </v-layout>
                        </v-form>
                    </v-stepper-content>
                    <!-- Additional Info -->
                    <v-stepper-content step="3">
                        <v-form
                            ref="additionalForm"
                            @submit.prevent="onSubmitAdditionalInfo"
                        >
                            <v-layout
                                row
                                wrap
                            >
                                <v-flex xs12>
                                    <v-textarea
                                        label="Reason for Return"
                                        v-model="form.comment"
                                    />
                                </v-flex>
                                <v-flex xs12>
                                    <v-btn
                                        class="ml-0"
                                        type="button"
                                        small
                                        @click="$refs.fileInput.click()"
                                    >
                                        Upload Product Photos
                                    </v-btn>
                                    <small>Max size: 20MB per file</small>
                                    <v-btn
                                        class="mx-3"
                                        small
                                        flat
                                        color="primary"
                                        @click="showSamplePhotos = true"
                                    >
                                        View Sample Photos
                                    </v-btn>
                                    <input
                                        ref="fileInput"
                                        style="display: none"
                                        type="file"
                                        multiple
                                        @change="onFileChange"
                                    >
                                </v-flex>
                                <v-flex xs12>
                                    <div v-if="form.files.length === 0">
                                        At least one photo required
                                    </div>
                                    <v-list
                                        two-line
                                    >
                                        <v-list-tile
                                            v-for="(file, index) in form.files"
                                            :key="file.name"
                                        >
                                            <v-list-tile-content>
                                                <v-list-tile-title>{{ file.name }}</v-list-tile-title>
                                                <v-list-tile-sub-title>{{ file.size | bytesFormat }}</v-list-tile-sub-title>
                                            </v-list-tile-content>

                                            <v-list-tile-action
                                                v-if="!file.fromPreviousSubmit"
                                            >
                                                <v-btn
                                                    type="button"
                                                    icon
                                                    :disabled="file.fromPreviousSubmit"
                                                    @click="form.files.splice(index, 1)"
                                                >
                                                    <v-icon size="18">
                                                        fa-times
                                                    </v-icon>
                                                </v-btn>
                                            </v-list-tile-action>
                                        </v-list-tile>
                                    </v-list>
                                </v-flex>
                            </v-layout>

                            <!-- Actions -->
                            <v-divider />
                            <v-layout
                                row
                                justify-space-between
                                mt-2
                            >
                                <v-flex shrink>
                                    <v-btn
                                        type="button"
                                        icon
                                        @click="step = 2"
                                    >
                                        <v-icon>fa-arrow-left</v-icon>
                                    </v-btn>
                                </v-flex>
                                <v-flex shrink>
                                    <v-btn
                                        type="submit"
                                        color="primary"
                                    >
                                        Next
                                    </v-btn>
                                </v-flex>
                            </v-layout>
                        </v-form>
                    </v-stepper-content>
                    <!-- Review -->
                    <v-stepper-content step="4">
                        <h3 class="title">
                            Return
                        </h3>
                        <return-info
                            :product-return="productReturnReview"
                            disable-file-download
                        />
                        <!-- Actions -->
                        <v-divider />
                        <v-layout
                            row
                            justify-space-between
                            mt-2
                        >
                            <v-flex shrink>
                                <v-btn
                                    type="button"
                                    icon
                                    :disabled="isSubmitting"
                                    @click="step = 3"
                                >
                                    <v-icon>fa-arrow-left</v-icon>
                                </v-btn>
                            </v-flex>
                            <v-flex shrink>
                                <v-btn
                                    type="submit"
                                    color="primary"
                                    :disabled="isSubmitting"
                                    :loading="isSubmitting"
                                    @click="onSubmitReturn"
                                >
                                    Submit
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-stepper-content>
                </v-stepper-items>
            </v-stepper>
        </template>
        <template v-else>
            <return-confirmation
                pre-submit
                @back="showSamplePhotos = false"
            />
        </template>
    </div>
</template>

<script>
import axios from 'axios'
import states from 'iso3166-2-db/regions/US/geonames.json'
import _ from 'lodash'
import ReturnInfo from './ReturnInfo'
import ReturnConfirmation from '../views/ContractorPortal/ReturnConfirmation'
import SnackBus from '../utils/buses/snackBus'
import { defaultError422, requestErrorMessage } from '../utils/constants/errors'

const headers = [
    {
        text: 'Product #',
        sortable: true,
        value: 'itemNumber'
    },
    {
        text: 'Product Name',
        sortable: true,
        value: 'description'
    },
    {
        text: 'Quantity to Return',
        sortable: false,
        value: 'quantity'
    }
]

export default {
    components: {
        ReturnInfo,
        ReturnConfirmation
    },
    props: {
        // only required for add wizard
        // invoice data is pulled from product return for edit wizard
        invoice: {
            type: Object,
            default: () => {}
        },
        isEdit: {
            type: Boolean,
            default: false
        },
        // only required for edit wizard
        productReturn: {
            type: Object,
            default: () => {}
        }
    },
    data () {
        let form
        if (this.isEdit) {
            const {
                customerName,
                company,
                address,
                city,
                state,
                zipcode,
                contactName,
                contactPhone,
                contactEmail,
                products,
                files
            } = this.productReturn

            form = {
                customerName,
                company,
                address,
                city,
                state,
                zipcode,
                contactName,
                contactPhone,
                contactEmail,
                products,
                files: files.map(item => ({
                    name: item.filename,
                    fromPreviousSubmit: true
                }))
            }
        } else {
            form = {
                customerName: null,
                // pickup location
                company: null,
                address: null,
                city: null,
                state: null,
                zipcode: null,
                // return contact
                contactName: null,
                contactPhone: null,
                contactEmail: null,
                comment: null,
                // set initial products from invoice
                products: this.invoice.products.map(product => {
                    return {
                        ...product,
                        quantityToReturn: 0
                    }
                }),
                files: []
            }
        }
        return {
            step: 1,
            headers,
            form,
            rules: {
                required: value => !!value || 'This field is required',
                positiveNumber: value => value >= 0 || 'This amount must be 0 or greater',
                lessThanOrEqual: max => value => value <= max || `This amount cannot exceed ${max}`,
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                },
                phone: value => (!!value && value.length === 10) || 'This phone number must be 10 digits'
            },
            states: _.orderBy(states, ['name'], ['asc']),
            isSubmitting: false,
            showTermsOfService: !this.isEdit, // initially open ToS dialog if add form
            agreeChecked: false,
            showSamplePhotos: false
        }
    },
    computed: {
        productReturnReview: function () {
            return {
                ...this.form,
                files: this.form.files.map(item => ({ filename: item.name })),
                internalFiles: []
            }
        }
    },
    methods: {
        onSubmitProductInfo () {
            if (!this.$refs.productForm.validate()) {
                SnackBus.showMessage(defaultError422, 'error')
                return
            }

            // check if atleast one product is being returned
            const foundReturn = this.form.products.find(product => product.quantityToReturn > 0)
            if (!foundReturn) {
                SnackBus.showMessage('At least one product must be returned.', 'error')
                return
            }

            this.step = 2
        },
        onSubmitCustomerInfo () {
            if (!this.$refs.customerForm.validate()) {
                SnackBus.showMessage(defaultError422, 'error')
                return
            }

            this.step = 3
        },
        onSubmitAdditionalInfo () {
            if (!this.$refs.additionalForm.validate()) {
                SnackBus.showMessage(defaultError422, 'error')
                return
            }

            if (this.form.files.length === 0) {
                SnackBus.showMessage('At least one photo must be provided.', 'error')
                return
            }

            // check file sizes
            for (let i = 0, length = this.form.files.length; i < length; i++) {
                const file = this.form.files[i]
                // if file is new & exceeds 20mb show error
                if (!file.fromPreviousSubmit && file.size > 20000000) {
                    SnackBus.showMessage(file.name + ' exceeds the 20MB limit')
                    return
                }
            }

            this.step = 4
        },
        onSubmitReturn () {
            if (this.isEdit) {
                this.onEdit()
            } else {
                this.onAdd()
            }
        },
        onAdd () {
            this.isSubmitting = true

            const formData = new FormData()

            const data = {
                invoiceNumber: this.invoice.invoiceNumber,
                ...this.form,
                products: this.form.products
                    .map(item => {
                        return {
                            itemNumber: item.itemNumber,
                            description: item.description,
                            invoiceDetailId: item.invoiceDetailId,
                            quantity: item.quantity,
                            quantityToReturn: item.quantityToReturn
                        }
                    }),
                // unset files
                files: undefined,
                metadata: {
                    invoiceNumber: this.invoice.invoiceNumber,
                    partnerNumber: this.invoice.partner.partnerNumber,
                    amountPaid: this.invoice.amountPaid,
                    salesRep: this.invoice.project.primaryRep.fullName,
                    salesRepEmail: this.invoice.project.primaryRep.login,
                    projectName: this.invoice.project.projectName
                }
            }
            formData.set('data', JSON.stringify(data))
            this.form.files.forEach(file => {
                formData.append('files', file)
            })

            axios.post('/api/returns', formData)
                .then(res => {
                    const { uuid } = res.data.data

                    return axios.get('/api/returns/' + uuid)
                })
                .then(res => {
                    const productReturn = res.data
                    this.$emit('created', productReturn)
                })
                .catch((err) => {
                    console.log('Product Wizard Add Error', err)
                    SnackBus.showMessage(requestErrorMessage, 'error')
                })
                .finally(() => {
                    this.isSubmitting = false
                })
        },
        onEdit () {
            this.isSubmitting = true

            const formData = new FormData()

            const data = {
                invoiceNumber: this.productReturn.invoiceNumber,
                ...this.form,
                products: this.form.products
                    .map(item => {
                        return {
                            id: item.id,
                            itemNumber: item.itemNumber,
                            description: item.description,
                            invoiceDetailId: item.invoiceDetailId,
                            quantity: item.quantity,
                            quantityToReturn: item.quantityToReturn
                        }
                    }),
                // unset files
                files: undefined,
                metadata: this.productReturn.metadata
            }

            formData.set('data', JSON.stringify(data))
            this.form.files.forEach(file => {
                formData.append('files', file)
            })

            axios.put('/api/returns/' + this.productReturn.uuid, formData)
                .then(res => {
                    const { uuid } = res.data.data
                    SnackBus.showMessage('Product Return successfully updated', 'success')

                    return axios.get('/api/returns/' + uuid)
                })
                .then(res => {
                    const productReturn = res.data
                    this.$emit('updated', productReturn)
                })
                .catch(err => {
                    console.log('Product Wizard Edit Error', err)
                    SnackBus.showMessage(requestErrorMessage, 'error')
                })
                .finally(() => {
                    this.isSubmitting = false
                })
        },
        onFileChange (e) {
            const files = e.target.files
            // ensure already chosen files are not duplicated in array
            this.form.files = _.unionBy(this.form.files, files, 'name')
            // reset input. this is needed for the case where a user selects a file,
            // deletes that file and tries to reselect the file. Without this
            // the @change event will not fire
            e.target.value = ''
        },
        onBackClick () {
            this.$emit('dialog-back', true)
        }
    }
}
</script>

<style lang="scss" scoped>
.wizard-stepper {
    /deep/ .v-icon {
        font-size: 14px;
    }
}
.product-input {
    /deep/ input {
        text-align: right;
    }
}
.shrink-column {
    width: 1%;
}
.product-table {
    /deep/ th i {
        margin-left: 4px;
    }
}
.dialog-checkbox {
    /deep/ .v-label {
        color: rgba(0,0,0,0.87);
    }
}
</style>
